import React, { useMemo, useState } from 'react';
import { Phone, Lock, User2 } from 'lucide-react';
import { Button } from './ui/button';
import { api } from '../services/api';
import { useWebAuth } from '../context/WebAuthContext';
import { useToast } from '../context/ToastContext';

export default function LoginForm({ onSuccess }) {
  const { loginWithToken } = useWebAuth();
  const { showToast } = useToast();

  const [tab, setTab] = useState('login'); // login | signup
  const [step, setStep] = useState('form'); // form | otp

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // login fields
  const [loginPhone, setLoginPhone] = useState('');
  const [loginPass, setLoginPass] = useState('');

  // signup fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [signupPhone, setSignupPhone] = useState('');
  const [pass1, setPass1] = useState('');
  const [pass2, setPass2] = useState('');
  const [otpCode, setOtpCode] = useState('');

  const phoneForOtp = signupPhone.trim();

  const canLogin = useMemo(() => {
    return !loading && loginPhone.trim().length >= 10 && loginPass.length >= 4;
  }, [loading, loginPhone, loginPass]);

  const canSignup = useMemo(() => {
    if (loading) return false;
    if (firstName.trim().length < 2) return false;
    if (lastName.trim().length < 2) return false;
    if (signupPhone.trim().length < 10) return false;
    if (pass1.length < 4) return false;
    if (pass1 !== pass2) return false;
    return true;
  }, [loading, firstName, lastName, signupPhone, pass1, pass2]);


  function resetState() {
    setError('');
    setLoading(false);
    setStep('form');
    setOtpCode('');
  }

  async function doLogin(e) {
    e?.preventDefault?.();
    setError('');
    setLoading(true);
    try {
      const res = await api.loginPassword(loginPhone.trim(), loginPass);

const token = res?.token;
const user = res?.user;

if (!token || !user) throw new Error('ورود ناموفق بود');
      loginWithToken(token, user);
      showToast({ title: 'خوش آمدید', message: 'با موفقیت وارد شدید.' });
      onSuccess?.();
      resetState();
    } catch (err) {
      const msg = err?.message || 'ورود ناموفق بود.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  async function requestOtp(e) {
    e?.preventDefault?.();
    if (!canSignup) return;
    setError('');
    setLoading(true);
    try {
      await api.requestOtp(phoneForOtp);
      setStep('otp');
      showToast({ title: 'کد ارسال شد', message: 'کد تایید را وارد کنید.' });
    } catch (err) {
      setError(err?.message || 'ارسال کد ناموفق بود.');
    } finally {
      setLoading(false);
    }
  }

  async function verifyOtp(e) {
    e?.preventDefault?.();
    setError('');
    setLoading(true);
    try {
      const res = await api.verifyOtp({
  phone: phoneForOtp,
  code: otpCode.trim(),
  first_name: firstName.trim(),
  last_name: lastName.trim(),
  password: pass1,
});

// چون بک‌اند { success, token, user } می‌فرستد:
const token = res?.token;
const user = res?.user;
if (!token || !user) throw new Error('ثبت‌نام ناموفق بود');
      loginWithToken(token, user);
      showToast({ title: 'ثبت‌نام موفق', message: 'اکانت شما ساخته شد.' });
      onSuccess?.();
      resetState();
    } catch (err) {
      // اگر قبلاً ثبت‌نام کرده باشد
      const msg = err?.message || 'کد تایید نامعتبر است.';
      if (String(msg).toLowerCase().includes('already') || String(msg).includes('قبلا')) {
        setTab('login');
        setStep('form');
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="w-full" dir="rtl">
      <div className="mx-auto w-full max-w-md">
        <div className="login-form-frame flex h-[600px] flex-col rounded-3xl border border-black/60 bg-white shadow-2xl">
          {/* header */}
          <div className="shrink-0 px-6 py-5 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-extrabold text-gray-900">ورود / ثبت‌نام</h2>
              <p className="text-xs text-gray-500 mt-1">برای ثبت پیش‌فاکتور باید وارد شوید.</p>
            </div>
          </div>

          {/* tabs */}
          <div className="shrink-0 px-6 pt-4">
            <div className="grid grid-cols-2 gap-2 rounded-2xl border border-blue-100 bg-blue-50/60 p-1">
              <button
                type="button"
                onClick={() => { setTab('login'); setStep('form'); setError(''); }}
                className={`h-11 rounded-2xl px-4 text-sm font-bold transition ${tab === 'login' ? 'bg-[#005bbb] text-white shadow-sm' : 'border border-gray-200 bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                ورود
              </button>
              <button
                type="button"
                onClick={() => { setTab('signup'); setStep('form'); setError(''); }}
                className={`h-11 rounded-2xl px-4 text-sm font-bold transition ${tab === 'signup' ? 'bg-[#005bbb] text-white shadow-sm' : 'border border-gray-200 bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                ثبت‌نام
              </button>
            </div>
          </div>

          {/* body */}
          <div className="login-form-body min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-5">
            {error && (
              <div className="mb-4 rounded-2xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            {tab === 'login' && (
              <form onSubmit={doLogin} className="flex min-h-full flex-col gap-3">
                <div>
                  <label className="text-xs text-gray-600">شماره همراه</label>
                  <div className="mt-1 flex items-center gap-2 rounded-2xl border border-gray-200 px-4 py-3">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <input
                      value={loginPhone}
                      onChange={(e) => setLoginPhone(e.target.value)}
                      className="flex-1 outline-none text-sm text-right"
                      placeholder="09xxxxxxxxx"
                      inputMode="tel"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-600">رمز عبور</label>
                  <div className="mt-1 flex items-center gap-2 rounded-2xl border border-gray-200 px-4 py-3">
                    <Lock className="w-4 h-4 text-gray-400" />
                    <input
                      value={loginPass}
                      onChange={(e) => setLoginPass(e.target.value)}
                      className="flex-1 outline-none text-sm text-right"
                      type="password"
                      placeholder="••••"
                    />
                  </div>
                </div>

                {/* <div className="rounded-2xl bg-blue-50 border border-blue-200 px-4 py-3 text-xs text-blue-800 leading-6">
                  <div className="flex items-center gap-2 font-bold">
                    <ShieldCheck className="w-4 h-4" />
                    حساب تست (بدون OTP)
                  </div>
                  <div className="mt-1">شماره: <span className="font-mono">09000000000</span> — رمز: <span className="font-mono">test1234</span></div>
                </div> */}

                <Button disabled={!canLogin} type="submit" className="mt-auto w-full shrink-0 rounded-2xl py-6 text-sm font-bold">
                  {loading ? 'در حال ورود...' : 'ورود'}
                </Button>
              </form>
            )}

            {tab === 'signup' && (
              <>
                {step === 'form' && (
                  <form onSubmit={requestOtp} className="flex min-h-full flex-col gap-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-gray-600">نام</label>
                        <div className="mt-1 flex items-center gap-2 rounded-2xl border border-gray-200 px-4 py-3">
                          <User2 className="w-4 h-4 text-gray-400" />
                          <input value={firstName} onChange={(e) => setFirstName(e.target.value)} className="flex-1 outline-none text-sm text-right" placeholder="حسین" />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-gray-600">نام خانوادگی</label>
                        <div className="mt-1 flex items-center gap-2 rounded-2xl border border-gray-200 px-4 py-3">
                          <User2 className="w-4 h-4 text-gray-400" />
                          <input value={lastName} onChange={(e) => setLastName(e.target.value)} className="flex-1 outline-none text-sm text-right" placeholder="زارعی" />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="text-xs text-gray-600">شماره همراه</label>
                      <div className="mt-1 flex items-center gap-2 rounded-2xl border border-gray-200 px-4 py-3">
                        <Phone className="w-4 h-4 text-gray-400" />
                        <input value={signupPhone} onChange={(e) => setSignupPhone(e.target.value)} className="flex-1 outline-none text-sm text-right" placeholder="09xxxxxxxxx" inputMode="tel" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-gray-600">رمز عبور</label>
                        <div className="mt-1 flex items-center gap-2 rounded-2xl border border-gray-200 px-4 py-3">
                          <Lock className="w-4 h-4 text-gray-400" />
                          <input value={pass1} onChange={(e) => setPass1(e.target.value)} className="flex-1 outline-none text-sm text-right" type="password" placeholder="حداقل 4 کاراکتر" />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-gray-600">تکرار رمز</label>
                        <div className="mt-1 flex items-center gap-2 rounded-2xl border border-gray-200 px-4 py-3">
                          <Lock className="w-4 h-4 text-gray-400" />
                          <input value={pass2} onChange={(e) => setPass2(e.target.value)} className="flex-1 outline-none text-sm text-right" type="password" placeholder="تکرار رمز" />
                        </div>
                      </div>
                    </div>

                    <Button disabled={!canSignup} type="submit" className="mt-auto w-full shrink-0 rounded-2xl py-6 text-sm font-bold">
                      {loading ? 'در حال ارسال کد...' : 'ارسال کد تایید'}
                    </Button>
                  </form>
                )}

                {step === 'otp' && (
                  <form onSubmit={verifyOtp} className="flex min-h-full flex-col gap-3">
                    <div className="text-sm text-gray-700 leading-7">
                      کد تایید ارسال‌شده به <span className="font-mono">{phoneForOtp}</span> را وارد کنید.
                    </div>
                    <div>
                      <label className="text-xs text-gray-600">کد تایید</label>
                      <input
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value)}
                        className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm text-center tracking-widest"
                        placeholder="123456"
                        inputMode="numeric"
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <Button type="button" variant="outline" className="rounded-2xl flex-1" onClick={() => { setStep('form'); setOtpCode(''); }}>
                        بازگشت
                      </Button>
                      <Button type="submit" className="rounded-2xl flex-1" disabled={loading || otpCode.trim().length < 4}>
                        {loading ? 'در حال تایید...' : 'تایید و ساخت اکانت'}
                      </Button>
                    </div>
                  </form>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
